import { setMostPlayed } from './MostPlayed'

const SEGMENT_COUNT = 50

const createSegments = () =>
  Array<number>(SEGMENT_COUNT).fill(0)

const createUniqueId = () =>
  crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random()}`

interface SessionOptions {
  videoId?: string
  enabled: boolean
}

export class MostPlayedTracker {
  private video: HTMLVideoElement | null = null

  private videoId: string | null = null
  private enabled = false

  private segments = createSegments()
  private lastSegment: number | null = null

  private uniqueId = createUniqueId()
  private lastSentSignature = ''

  bind(video: HTMLVideoElement) {
    if (this.video === video) return

    this.unbind()

    this.video = video

    video.addEventListener(
      'timeupdate',
      this.handleTimeUpdate
    )

    video.addEventListener(
      'seeked',
      this.handleSeeked
    )

    video.addEventListener(
      'pause',
      this.handlePause
    )

    video.addEventListener(
      'ended',
      this.handleEnded
    )
  }

  unbind() {
    const video = this.video
    if (!video) return

    video.removeEventListener(
      'timeupdate',
      this.handleTimeUpdate
    )

    video.removeEventListener(
      'seeked',
      this.handleSeeked
    )

    video.removeEventListener(
      'pause',
      this.handlePause
    )

    video.removeEventListener(
      'ended',
      this.handleEnded
    )

    this.video = null
  }

  setSession({
    videoId,
    enabled
  }: SessionOptions) {
    const nextVideoId =
      videoId ? String(videoId) : null

    /*
     * Same physical media/session.
     *
     * Feed -> fullscreen -> feed must NOT reset replay tracking.
     */
    if (
      nextVideoId &&
      nextVideoId === this.videoId
    ) {
      this.enabled = enabled
      return
    }

    /*
     * Flush previous media before starting another one.
     */
    void this.flush()

    this.videoId = nextVideoId
    this.enabled =
      enabled && Boolean(nextVideoId)

    this.segments = createSegments()
    this.lastSegment = null
    this.uniqueId = createUniqueId()
    this.lastSentSignature = ''
  }

private readonly handleTimeUpdate = () => {

  this.recordCurrentPosition()
}

  private readonly handleSeeked = () => {
    /*
     * Do not count all segments jumped over by a seek.
     *
     * The next timeupdate begins tracking at the new position.
     */
    this.lastSegment = null

    void this.flush()
  }

  private readonly handlePause = () => {
    void this.flush()
  }

  private readonly handleEnded = () => {
    /*
     * Make sure the final segment is captured.
     */
    this.recordCurrentPosition(true)

    void this.flush()
  }

  private recordCurrentPosition(
    allowEnded = false
  ) {
    if (!this.enabled) return

    const video = this.video
    if (!video) return

    if (video.seeking) return
    if (video.paused && !allowEnded) return
    if (video.ended && !allowEnded) return

    const duration = video.duration
    const time = video.currentTime

    if (
      !Number.isFinite(duration) ||
      duration <= 0 ||
      !Number.isFinite(time) ||
      time < 0
    ) {
      return
    }

    const segmentDuration =
      duration / SEGMENT_COUNT

    if (segmentDuration <= 0) return

    const index = Math.min(
      SEGMENT_COUNT - 1,
      Math.max(
        0,
        Math.floor(
          time / segmentDuration
        )
      )
    )

    /*
     * First observed segment.
     */
    if (this.lastSegment === null) {
      this.segments[index] += 1
      this.lastSegment = index
      return
    }

    /*
     * Same segment: nothing to do.
     *
     * timeupdate fires multiple times per second, so we must not
     * increment repeatedly while sitting inside one segment.
     */
    if (index === this.lastSegment) {
      return
    }

    /*
     * Normal forward playback.
     *
     * A slow timeupdate can cross more than one 2% segment,
     * so fill the crossed segments.
     */
    if (index > this.lastSegment) {
      for (
        let i = this.lastSegment + 1;
        i <= index;
        i++
      ) {
        this.segments[i] += 1
      }

      this.lastSegment = index
      return
    }

    /*
     * Backward movement.
     *
     * Normally seeked resets lastSegment first, but this protects
     * against browsers that emit events in a different order.
     */
    this.segments[index] += 1
    this.lastSegment = index
  }

  async flush() {
    if (!this.enabled) return
    if (!this.videoId) return

    const segments = [...this.segments]

    /*
     * Never send:
     *
     * [0, 0, 0, ...]
     */
    if (
      !segments.some(
        value => value > 0
      )
    ) {
      return
    }

    /*
     * pause -> seek -> pause can fire close together.
     *
     * Do not send the identical cumulative state repeatedly.
     */
    const signature =
      segments.join(',')

    if (
      signature ===
      this.lastSentSignature
    ) {
      return
    }

    this.lastSentSignature =
      signature

    try {
      await setMostPlayed({
        video_id: this.videoId,
        unique_id: this.uniqueId,
        most_played: segments
      })
    } catch (error) {
      /*
       * Failed request may be retried on the next pause/seek/etc.
       */
      if (
        this.lastSentSignature ===
        signature
      ) {
        this.lastSentSignature = ''
      }

      console.error(
        '[MostPlayed] Failed to send replay data',
        error
      )
    }
  }

  getSegments() {
    return [...this.segments]
  }

  destroy() {
    void this.flush()

    this.unbind()

    this.videoId = null
    this.enabled = false
    this.segments = createSegments()
    this.lastSegment = null
    this.lastSentSignature = ''
  }
}